import React, { useEffect, useRef, useState } from "react"
import Slider from "react-slick/lib/slider"
import { useTranslation } from "@zestia/gatsby-plugin-react-i18next"
import salesPipelineImage from "@/images/homepage/sales-pipeline.png"
import emailMarketingImage from "@/images/homepage/email-marketing.png"
import salesAnalyticsImage from "@/images/homepage/sales-analytics.png"
import projectsImage from "@/images/homepage/projects.png"
import contactManagementImage from "@/images/homepage/contact-management.png"
import workflowAutomationImage from "@/images/homepage/workflow-automation.png"

const FeaturesCarousel = () => {
  const { t } = useTranslation()

  const [controlSlider, setcontrolSlider] = useState(0)
  const [mainSlider, setmainSlider] = useState(0)
  const [currentSlide, setcurrentSlide] = useState(0)

  const [mainSliderHasLoaded, setMainSliderHasLoaded] = useState(false)

  let controlsRef = useRef(null)
  let sliderRef = useRef(null)

  // Updated slides data
  const slides = [
    {
      title: t("navigation.features.feature-1.title"),
      image: contactManagementImage,
      alt: t("home.features-carousel-slides.contact-management"),
    },
    {
      title: t("navigation.features.feature-4.title"),
      image: salesPipelineImage,
      alt: t("home.features-carousel-slides.sales-pipeline"),
    },
    {
      title: t("integrations.categories.email-marketing"),
      image: emailMarketingImage,
      alt: t("home.features-carousel-slides.email-marketing"),
    },
    {
      title: t("navigation.features.feature-5.title"),
      image: salesAnalyticsImage,
      alt: t("home.features-carousel-slides.sales-analytics"),
    },
    {
      title: t("navigation.features.feature-6.title"),
      image: projectsImage,
      alt: t("home.features-carousel-slides.projects"),
    },
    {
      title: t("navigation.features.feature-7.title"),
      image: workflowAutomationImage,
      alt: t("home.features-carousel-slides.workflow-automation"),
    },
  ]

  useEffect(() => {
    setcontrolSlider(controlsRef)
    setmainSlider(sliderRef)
  }, [])

  const [screenReaderSlideDescription, setScreenReaderSlideDescription] =
    useState("")

  useEffect(() => {
    // Note: We set the description here as a whole, as the aria-live region will only read out what has changed (currentSlide - ie. 2) instead of the entire sentence otherwise.
    setScreenReaderSlideDescription(
      `Slide ${currentSlide + 1} of ${slides.length}`,
    )
  }, [currentSlide])

  return (
    <div className="features-carousel container-fluid row center-xs">
      {/* Control slider */}
      <Slider
        asNavFor={mainSlider}
        arrows={false}
        ref={slider => (controlsRef = slider)}
        slidesToShow={5}
        centerMode={true}
        draggable={false}
        focusOnSelect={true}
        variableWidth={true}
        initialSlide={0}
        className={`col-xs-12 col-md-6 features-carousel__controls ${
          !mainSliderHasLoaded ? "features-carousel__controls--loading" : ""
        }`}
      >
        {slides.map((slide, index) => (
          <button
            key={index}
            tabIndex="0"
            aria-label={`${slide.title} - ${index + 1} of ${slides.length}`}
            className={`features-carousel__control
              ${
                currentSlide === index
                  ? "features-carousel__control--active"
                  : ""
              }
            `}
          >
            {slide.title}
          </button>
        ))}
      </Slider>

      {/* Image slider */}
      <div className="features-carousel__slider-wrapper col-xs-12 mt-6">
        {!mainSliderHasLoaded && (
          <div className="features-carousel__slider">
            <img src={slides[0].image} alt={slides[0].alt} loading="eager" />
          </div>
        )}

        <p className="screen-reader-only" aria-live="polite">
          {screenReaderSlideDescription}
        </p>

        {/* Main slider */}
        <Slider
          asNavFor={controlSlider}
          arrows={false}
          ref={slider => (sliderRef = slider)}
          beforeChange={(_, newIndex) => setcurrentSlide(newIndex)}
          initialSlide={0}
          className={`features-carousel__slider ${
            !mainSliderHasLoaded ? "features-carousel__slider--loading" : ""
          }`}
          swipe={false}
          onInit={() => setMainSliderHasLoaded(true)}
        >
          {slides.map((slide, index) => (
            <div
              key={index}
              className="features-carousel__slide"
              onClick={() => sliderRef.slickNext()}
            >
              <img src={slide.image} alt={slide.alt} loading="eager" />
            </div>
          ))}
        </Slider>

        {/* Prev/Next Arrows */}
        {mainSliderHasLoaded && (
          <>
            <button
              className="features-carousel__arrow features-carousel__arrow--prev"
              onClick={() => sliderRef.slickPrev()}
              aria-label={t("common.previous-slide")}
            >
              <svg
                width="32"
                height="32"
                viewBox="0 0 32 32"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M10.2933 15.2933C10.1061 15.4808 10.0009 15.735 10.0009 16C10.0009 16.265 10.1061 16.5192 10.2933 16.7067L20.2933 26.7067C20.4829 26.8833 20.7336 26.9795 20.9927 26.9749C21.2518 26.9703 21.499 26.8654 21.6822 26.6822C21.8654 26.4989 21.9703 26.2518 21.9749 25.9927C21.9795 25.7336 21.8833 25.4829 21.7067 25.2933L12.4133 16L21.7067 6.70667C21.8833 6.5171 21.9795 6.26637 21.9749 6.0073C21.9703 5.74824 21.8654 5.50106 21.6822 5.31784C21.499 5.13462 21.2518 5.02967 20.9927 5.0251C20.7336 5.02053 20.4829 5.11669 20.2933 5.29333L10.2933 15.2933Z"
                  fill="#64748B"
                />
              </svg>
            </button>

            <button
              className="features-carousel__arrow features-carousel__arrow--next"
              onClick={() => sliderRef.slickNext()}
              aria-label={t("common.next-slide")}
            >
              <svg
                width="32"
                height="32"
                viewBox="0 0 32 32"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{
                  transform: "rotate(180deg)",
                }}
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M10.2933 15.2933C10.1061 15.4808 10.0009 15.735 10.0009 16C10.0009 16.265 10.1061 16.5192 10.2933 16.7067L20.2933 26.7067C20.4829 26.8833 20.7336 26.9795 20.9927 26.9749C21.2518 26.9703 21.499 26.8654 21.6822 26.6822C21.8654 26.4989 21.9703 26.2518 21.9749 25.9927C21.9795 25.7336 21.8833 25.4829 21.7067 25.2933L12.4133 16L21.7067 6.70667C21.8833 6.5171 21.9795 6.26637 21.9749 6.0073C21.9703 5.74824 21.8654 5.50106 21.6822 5.31784C21.499 5.13462 21.2518 5.02967 20.9927 5.0251C20.7336 5.02053 20.4829 5.11669 20.2933 5.29333L10.2933 15.2933Z"
                  fill="#64748B"
                />
              </svg>
            </button>
          </>
        )}
      </div>
    </div>
  )
}

export default FeaturesCarousel
